/**
 * Grid cells for the adaptive entry surface. Enterable-ness and value status are two separate channels (#394):
 *  - locked       → hatched grey tile with a dash, NON-focusable (the BUG-13 affordance fix); "n/a" when the
 *                   farm turned that field off at the slot (#371)
 *  - enterable    → white boxed input with a hint of the shape to type; dashed blue when it is due
 *  - status       → coloured left edge once it holds a value (green only for a field with a range);
 *                   warned/critical also tint the fill and the value from the validation tier (still saves)
 *  - error        → red ring while a hard-reject sits uncorrected, or while typed
 *                   text is not accepted: not a number, a decimal in a whole-number
 *                   column, a comma that could be 1.25 or 1250 (nothing is saved; the
 *                   text stays to fix, with the reason under it).
 *                   A refused value also says "Not saved" in the cell; the full
 *                   sentence is the cell's title and the grid's row note.
 * Visuals use the app's own Tailwind tokens — NOT the design packet's skin.
 */
import { useId, useState } from 'react';
import { CircleAlert } from 'lucide-react';
import { cn } from '@/lib/cn';
import { entryHint, type ParamDef, type Tier } from '@/lib/waterModel';
import { formatPair, pairChanges, parsePairInput } from '@/lib/pairs';
import { parseGridValue } from './parseGridValue';

/** Where a cell sits in time: the column of the current round, and an empty cell whose round has started. */
interface CellMoment {
  now?: boolean;
  due?: boolean;
}

const tdClass = (now?: boolean) => cn('border-t border-gray-100 p-1 align-top', now && 'bg-primary-600/[0.07]');

const lockedTile =
  'flex h-8 select-none items-center justify-center rounded-sm text-[13px] text-gray-500 cursor-not-allowed ' +
  'bg-[repeating-linear-gradient(135deg,#e5e7eb_0_5px,#d1d5db_5px_7px)]';

/** A cell this slot never takes: a dash, no focus. */
export function LockedCell({ now }: CellMoment) {
  return (
    <td aria-disabled data-locked title="Not taken at this round" className={tdClass(now)}>
      <div className={lockedTile}>–</div>
    </td>
  );
}

/** A cell the slot takes but this farm turned off: "n/a", and the reason on hover. */
export function FarmOffCell({ now, reason }: CellMoment & { reason: string }) {
  return (
    <td aria-disabled data-farm-off title={reason} className={tdClass(now)}>
      <div className={lockedTile}>
        <span className="rounded-sm bg-gray-200 px-1.5 py-px text-[10px] font-semibold tracking-wide">n/a</span>
      </div>
    </td>
  );
}

// The left edge says how the saved value sits against the farm's range; only warning and critical tint the box.
const tierEdgeClass: Record<Tier, string> = {
  ok: 'border-l-4 border-l-emerald-600',
  warning: 'border-l-4 border-l-amber-600 bg-amber-50',
  critical: 'border-l-4 border-l-red-600 bg-red-50',
};

const tierTextClass: Record<Tier, string> = {
  ok: 'text-gray-900 font-semibold',
  warning: 'text-amber-800 font-semibold',
  critical: 'text-red-700 font-semibold',
};

interface BoxState {
  tier: Tier;
  filled: boolean;
  judged: boolean;
  bad: boolean;
  due?: boolean;
}

function boxClass({ tier, filled, judged, bad, due }: BoxState) {
  return cn(
    'rounded border bg-white shadow-[inset_0_1px_1px_rgba(0,0,0,0.06)] focus-within:ring-2 focus-within:ring-primary-400',
    bad
      ? 'border-red-400 bg-red-50 ring-2 ring-red-400'
      : due && !filled
        ? 'border-[1.5px] border-dashed border-primary-600 bg-primary-50'
        : 'border-gray-400',
    filled && !bad && (tier !== 'ok' || judged) && tierEdgeClass[tier]
  );
}

const inputClass = (due: boolean | undefined, filled: boolean, tier: Tier, saving?: boolean) =>
  cn(
    'h-8 w-full bg-transparent px-2 text-right text-sm tabular-nums outline-none transition-colors',
    due && !filled ? 'placeholder:text-primary-600' : 'placeholder:text-gray-400',
    tierTextClass[tier],
    saving && 'opacity-70'
  );

interface NumberCellProps extends CellMoment {
  cellId: string;
  param: ParamDef;
  value: number | null;
  tier: Tier;
  /** The farm has a range for this field, so a value inside it earns the green edge. */
  judged: boolean;
  rejected: boolean;
  refusedMessage?: string | null;
  refusedNoteId?: string;
  saving?: boolean;
  step?: number;
  /** Transparency only (#374): "clear to bottom" is entered instead of a number; `text` is what the cell shows (#396). */
  clearToBottom?: { on: boolean; text: string; caption?: string; onToggle: (on: boolean) => void };
  onCommit: (value: number | null) => void;
  onNavKey: (cellId: string, key: string) => void;
  onPasteColumn: (cellId: string, text: string) => void;
  registerRef: (cellId: string, el: HTMLInputElement | HTMLButtonElement | null) => void;
}

export function NumberCell({
  cellId,
  param,
  value,
  tier,
  judged,
  now,
  due,
  rejected,
  refusedMessage,
  refusedNoteId,
  saving,
  step,
  clearToBottom,
  onCommit,
  onNavKey,
  onPasteColumn,
  registerRef,
}: NumberCellProps) {
  // Uncontrolled-with-seed: local string is the edit buffer. The parent re-keys
  // this cell on external value changes (copy-prev / paste / refetch) so a fresh
  // mount re-seeds — no setState-in-effect.
  const [local, setLocal] = useState(value === null ? '' : String(value));

  const [typedError, setTypedError] = useState<string | null>(null);
  const messageId = useId();
  const notSavedId = useId();

  // Tabbing through a cell without changing it must send nothing: a blank
  // "committed" over an empty slot would create an all-null row.
  const commit = (): boolean => {
    const parsed = parseGridValue(param, local);
    if ('error' in parsed) {
      setTypedError(parsed.error);
      return false;
    }
    setTypedError(null);
    if (parsed.value === null) {
      if (value !== null) onCommit(null); // NULL ≠ 0 — a blank persists as null
      return true;
    }
    // The cell is only re-seeded when its value changes: typed 1.25 over a saved 1.3 must still read 1.3.
    setLocal(String(parsed.value));
    if (parsed.value !== value) onCommit(parsed.value);
    return true;
  };

  const describedBy = [
    typedError ? messageId : null,
    rejected ? (refusedMessage && refusedNoteId ? refusedNoteId : notSavedId) : null,
  ].filter(Boolean).join(' ');

  if (clearToBottom?.on) {
    return (
      <td className={tdClass(now)}>
        <button
          type="button"
          ref={(el) => registerRef(cellId, el)}
          data-clear-to-bottom={cellId}
          title="Clear to bottom. Click to type a number instead"
          onClick={() => clearToBottom.onToggle(false)}
          className={cn(
            'w-full whitespace-nowrap rounded border border-slate-400 bg-slate-200 px-2 text-right text-sm font-medium text-slate-700 outline-none',
            clearToBottom.caption ? 'min-h-8 py-0.5' : 'h-8',
            'focus:ring-2 focus:ring-primary-400',
            saving && 'opacity-70'
          )}
        >
          {clearToBottom.text}
          {clearToBottom.caption && (
            <span data-clear-caption className="block text-[10px] font-normal leading-tight text-slate-600">
              {clearToBottom.caption}
            </span>
          )}
        </button>
      </td>
    );
  }

  return (
    <td className={tdClass(now)}>
      <div className={boxClass({ tier, filled: value !== null, judged, bad: rejected || typedError !== null, due })} data-box>
        <input
          ref={(el) => registerRef(cellId, el)}
          data-cell={cellId}
          inputMode="decimal"
          step={step}
          value={local}
          placeholder={entryHint(param)}
          title={refusedMessage ?? undefined}
          aria-invalid={rejected || typedError !== null}
          aria-describedby={describedBy || undefined}
          onChange={(e) => {
            setLocal(e.target.value);
            setTypedError(null);
          }}
          onBlur={() => {
            commit();
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === 'ArrowDown' || e.key === 'ArrowUp') {
              e.preventDefault();
              if (commit()) onNavKey(cellId, e.key);
            } else if (e.key === 'Escape') {
              setTypedError(null);
              setLocal(value === null ? '' : String(value));
              (e.target as HTMLInputElement).blur();
            }
          }}
          onPaste={(e) => {
            const text = e.clipboardData.getData('text');
            if (/[\n\t]/.test(text)) {
              e.preventDefault();
              onPasteColumn(cellId, text);
            }
          }}
          className={inputClass(due, value !== null, tier, saving)}
        />
      </div>
      {clearToBottom && (
        <button
          type="button"
          data-clear-to-bottom-set={cellId}
          title="Clear to bottom: the water is clear to the bottom, so there is no reading"
          onClick={() => clearToBottom.onToggle(true)}
          className="mt-0.5 block w-full px-1 text-right text-[10px] font-semibold leading-tight text-primary-600 hover:text-primary-800"
        >
          Clear to bottom
        </button>
      )}
      {rejected && (
        <p
          id={notSavedId}
          data-not-saved
          className="flex items-center justify-end gap-0.5 px-1.5 pb-1 text-[10px] font-semibold leading-tight text-red-700"
        >
          <CircleAlert className="h-3 w-3 shrink-0" aria-hidden="true" />
          Not saved
        </p>
      )}
      {typedError && (
        <p id={messageId} role="alert" className="px-1.5 pb-1 text-right text-[10px] leading-tight text-red-600">
          {typedError}
        </p>
      )}
    </td>
  );
}

interface SelectCellProps {
  cellId: string;
  value: string | null;
  options: { value: string; label: string }[];
  onCommit: (value: string | null) => void;
  registerRef: (cellId: string, el: HTMLSelectElement | null) => void;
}

export function SelectCell({ cellId, value, options, onCommit, registerRef }: SelectCellProps) {
  // Observations never carry a tier and never gate completion.
  return (
    <td className="border border-gray-100 bg-primary-50/60 p-0">
      <select
        ref={(el) => registerRef(cellId, el)}
        value={value ?? ''}
        onChange={(e) => onCommit(e.target.value || null)}
        className="w-full bg-transparent px-1.5 py-1.5 text-sm text-gray-900 outline-none focus:ring-2 focus:ring-inset focus:ring-primary-400"
      >
        <option value="">—</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </td>
  );
}

interface PairCellProps extends CellMoment {
  cellId: string;
  first: number | null;
  second: number | null;
  tier: Tier;
  judged: boolean;
  /** What to type in an empty cell ("000/000"). */
  placeholder: string;
  rejected: boolean;
  refusedMessage?: string | null;
  refusedNoteId?: string;
  hint?: string;
  saving?: boolean;
  /** A side that changed, with its new value (null = cleared). */
  onCommit: (changes: { first?: number | null; second?: number | null }) => void;
  onNavKey: (cellId: string, key: string) => void;
  onPasteColumn: (cellId: string, text: string) => void;
  registerRef: (cellId: string, el: HTMLInputElement | null) => void;
}

/** One cell for a pair (alkalinity 1/2, calcium/magnesium): shows and takes "x/y"; stored as two columns. */
export function PairCell({
  cellId,
  first,
  second,
  tier,
  judged,
  placeholder,
  now,
  due,
  rejected,
  refusedMessage,
  refusedNoteId,
  hint,
  saving,
  onCommit,
  onNavKey,
  onPasteColumn,
  registerRef,
}: PairCellProps) {
  const [local, setLocal] = useState(formatPair(first, second) ?? '');
  const [typedError, setTypedError] = useState<string | null>(null);
  const messageId = useId();
  const notSavedId = useId();

  const commit = (): boolean => {
    const typed = local.trim();
    // Tabbing through an untouched cell sends nothing; an emptied one clears both numbers.
    if (typed === (formatPair(first, second) ?? '')) return true;
    const input = parsePairInput(local);
    if ('error' in input) {
      setTypedError(input.error);
      return false;
    }
    setTypedError(null);
    const changes = pairChanges(input, { first, second }, rejected);
    if (changes.first !== undefined || changes.second !== undefined) onCommit(changes);
    // Show what the cell will hold once saved (a blank side keeps its saved number).
    const nextFirst = changes.first !== undefined ? changes.first : first;
    const nextSecond = changes.second !== undefined ? changes.second : second;
    setLocal(formatPair(nextFirst, nextSecond) ?? '');
    return true;
  };

  const describedBy = [
    typedError ? messageId : null,
    rejected ? (refusedMessage && refusedNoteId ? refusedNoteId : notSavedId) : null,
  ].filter(Boolean).join(' ');
  const bad = rejected || typedError !== null;

  return (
    <td className={tdClass(now)}>
      <div className={boxClass({ tier, filled: first !== null || second !== null, judged, bad, due })} data-box>
        <input
          ref={(el) => registerRef(cellId, el)}
          data-cell={cellId}
          data-pair-cell
          inputMode="text"
          value={local}
          placeholder={placeholder}
          title={refusedMessage ?? `${hint ?? 'Type both numbers like 140/120'}. Leave a side blank to keep it, type - to clear it`}
          aria-invalid={bad}
          aria-describedby={describedBy || undefined}
          onChange={(e) => {
            setLocal(e.target.value);
            setTypedError(null);
          }}
          onBlur={() => {
            commit();
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === 'ArrowDown' || e.key === 'ArrowUp') {
              e.preventDefault();
              if (commit()) onNavKey(cellId, e.key);
            } else if (e.key === 'Escape') {
              setTypedError(null);
              setLocal(formatPair(first, second) ?? '');
              (e.target as HTMLInputElement).blur();
            }
          }}
          onPaste={(e) => {
            const text = e.clipboardData.getData('text');
            if (/[\n\t]/.test(text)) {
              e.preventDefault();
              onPasteColumn(cellId, text);
            }
          }}
          className={inputClass(due, first !== null || second !== null, tier, saving)}
        />
      </div>
      {rejected && (
        <p id={notSavedId} data-not-saved className="flex items-center justify-end gap-0.5 px-1.5 pb-1 text-[10px] font-semibold leading-tight text-red-700">
          <CircleAlert className="h-3 w-3 shrink-0" aria-hidden="true" />
          Not saved
        </p>
      )}
      {typedError && (
        <p id={messageId} role="alert" className="px-1.5 pb-1 text-right text-[10px] leading-tight text-red-600">
          {typedError}
        </p>
      )}
    </td>
  );
}
